import { createBrowserRouter, redirect, type RouteObject } from 'react-router';
import LoginPage from './pages/login';
import AppConfigPage from './pages/app-config';
import CategoriesPage from './pages/categories';
import CurrenciesPage from './pages/currencies';
import ExpensesPage from './pages/expenses';
import FeedbackPage from './pages/feedback';
import GroupsPage from './pages/groups';
import NotificationsPage from './pages/notifications';
import PagesPage from './pages/pages';
import UsersPage from './pages/users';
import AppUpdatePage, { loader as appUpdateLoader } from './pages/app-update';
import AuditLogPage, { loader as auditLogLoader } from './pages/audit-log';
import DashboardPage, { loader as dashboardLoader } from './pages/dashboard';
import ExpenseDetailPage, { loader as expenseLoader } from './pages/expense-detail';
import FeedbackDetailPage, { loader as feedbackLoader } from './pages/feedback-detail';
import GroupDetailPage, { loader as groupLoader } from './pages/group-detail';
import RecurringDetailPage, { loader as recurringLoader } from './pages/recurring-detail';
import SurveyPage, { loader as surveyLoader } from './pages/survey-detail';
import NewSurveyPage from './pages/survey-new';
import SurveyReportPage, { loader as surveyReportLoader } from './pages/survey-report';
import SurveysPage, { loader as surveysLoader } from './pages/surveys';
import TeamPage, { loader as teamLoader } from './pages/team';
import UserDetailPage, { loader as userLoader } from './pages/user-detail';
import { publicRoutes } from './public-routes';
import { ADMIN_ROUTE_ID, loadSession, redirectIfSignedIn } from './session';
import { DashboardShell, RouteError } from './screens';

const notFound = () => {
  throw new Response('Not Found', { status: 404, statusText: 'Not Found' });
};

// Screens inside the signed-in frame. Paths are relative to /admin and
// match the addresses the Next.js admin used, so bookmarks keep working.
const screens: RouteObject[] = [
  { index: true, loader: dashboardLoader, element: <DashboardPage /> },

  { path: 'users', element: <UsersPage /> },
  { path: 'users/:id', loader: userLoader, element: <UserDetailPage /> },
  { path: 'groups', element: <GroupsPage /> },
  { path: 'groups/:id', loader: groupLoader, element: <GroupDetailPage /> },
  { path: 'expenses', element: <ExpensesPage /> },
  { path: 'expenses/:id', loader: expenseLoader, element: <ExpenseDetailPage /> },
  { path: 'recurring/:id', loader: recurringLoader, element: <RecurringDetailPage /> },
  { path: 'categories', element: <CategoriesPage /> },
  { path: 'currencies', element: <CurrenciesPage /> },

  { path: 'feedback', element: <FeedbackPage /> },
  { path: 'feedback/:id', loader: feedbackLoader, element: <FeedbackDetailPage /> },
  { path: 'surveys', loader: surveysLoader, element: <SurveysPage /> },
  { path: 'surveys/new', element: <NewSurveyPage /> },
  { path: 'surveys/:id', loader: surveyLoader, element: <SurveyPage /> },
  { path: 'surveys/:id/report', loader: surveyReportLoader, element: <SurveyReportPage /> },
  { path: 'notifications', element: <NotificationsPage /> },

  { path: 'pages', element: <PagesPage /> },
  { path: 'app-config', element: <AppConfigPage /> },
  { path: 'app-update', loader: appUpdateLoader, element: <AppUpdatePage /> },
  { path: 'settings/team', loader: teamLoader, element: <TeamPage /> },
  { path: 'audit-log', loader: auditLogLoader, element: <AuditLogPage /> },

  { path: '*', loader: notFound },
];

export const routes: RouteObject[] = [
  { path: '/', loader: () => redirect('/admin/login') },
  ...publicRoutes,
  { path: '/admin/login', loader: redirectIfSignedIn, element: <LoginPage /> },
  {
    id: ADMIN_ROUTE_ID,
    path: '/admin',
    loader: loadSession,
    element: <DashboardShell />,
    errorElement: <RouteError />,
    // A screen that fails shows its error inside the frame, sidebar intact.
    children: [{ errorElement: <RouteError />, children: screens }],
  },
  // Any other address: the not-found screen.
  { path: '*', loader: notFound, errorElement: <RouteError /> },
];

export const router = createBrowserRouter(routes);
